import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsFileEarmarkText, BsDownload, BsArrowsMove, BsTrash, BsArrowClockwise, BsFiles } from 'react-icons/bs';
import { FiUploadCloud, FiRotateCw } from 'react-icons/fi';
import { PDFDocument, degrees } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import SEO from '../components/SEO';
import { pdfOrganizeSchemas } from '../schemas';
import './ToolPage.css';

// Set up PDF.js worker - use local npm package instead of CDN to avoid Chrome permission popup
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const PDFOrganize = () => {
  const [file, setFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pages, setPages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [draggedPage, setDraggedPage] = useState(null);
  const [pageThumbnails, setPageThumbnails] = useState({});
  const fileInputRef = useRef(null);
  const pdfDocumentRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      await loadPDF(selectedFile);
    }
  };

  const loadPDF = async (pdfFile) => {
    try {
      // Read file as ArrayBuffer for pdf-lib
      const arrayBuffer = await pdfFile.arrayBuffer();
      
      // Load with pdf-lib for manipulation
      const pdf = await PDFDocument.load(arrayBuffer);
      const pageCount = pdf.getPageCount();
      
      const pagesData = Array.from({ length: pageCount }, (_, i) => ({
        id: i + 1,
        pageNum: i + 1,
        rotation: 0,
        deleted: false
      }));
      
      setFile(pdfFile);
      setPdfDoc(pdf);
      setPages(pagesData);

      // Load PDF for thumbnail generation with pdfjs
      // Create a copy of the arrayBuffer for pdfjs
      const pdfData = new Uint8Array(await pdfFile.arrayBuffer());
      const loadingTask = pdfjsLib.getDocument({ data: pdfData });
      const pdfDocument = await loadingTask.promise;
      pdfDocumentRef.current = pdfDocument;

      // Generate thumbnails for all pages
      generateThumbnails(pdfDocument, pageCount);
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Error loading PDF. Please try again.');
    }
  };

  const generateThumbnails = async (pdfDocument, pageCount) => {
    const thumbnails = {};
    
    for (let i = 1; i <= pageCount; i++) {
      try {
        const page = await pdfDocument.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        thumbnails[i] = canvas.toDataURL();
      } catch (error) {
        console.error(`Error generating thumbnail for page ${i}:`, error);
      }
    }
    
    setPageThumbnails(thumbnails);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfDocumentRef.current) {
        try {
          pdfDocumentRef.current.destroy();
        } catch (error) {
          console.log('PDF cleanup:', error);
        }
      }
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      await loadPDF(droppedFile);
    }
  };

  const handlePageDragStart = (e, index) => {
    setDraggedPage(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePageDragOver = (e, index) => {
    e.preventDefault();
    if (draggedPage === null || draggedPage === index) return;

    const newPages = [...pages];
    const draggedItem = newPages[draggedPage];
    newPages.splice(draggedPage, 1);
    newPages.splice(index, 0, draggedItem);
    
    setPages(newPages);
    setDraggedPage(index);
  };

  const handlePageDragEnd = () => {
    setDraggedPage(null);
  };

  const rotatePage = (index, angle) => {
    setPages(pages.map((page, i) => 
      i === index ? { ...page, rotation: (page.rotation + angle) % 360 } : page
    ));
  };

  const deletePage = (index) => {
    setPages(pages.map((page, i) => 
      i === index ? { ...page, deleted: true } : page
    ));
  };

  const duplicatePage = (index) => {
    const newPages = [...pages];
    const duplicatedPage = { ...pages[index], id: Date.now() };
    newPages.splice(index + 1, 0, duplicatedPage);
    setPages(newPages);
  };

  const undoDelete = (index) => {
    setPages(pages.map((page, i) => 
      i === index ? { ...page, deleted: false } : page
    ));
  };

  const savePDF = async () => {
    if (!pdfDoc) return;

    const activePages = pages.filter(page => !page.deleted);
    if (activePages.length === 0) {
      alert('No pages to save. Please keep at least one page.');
      return;
    }

    setIsProcessing(true);

    try {
      // Start the minimum 3-second timer
      const startTime = Date.now();

      const newPdf = await PDFDocument.create();
      
      for (const pageData of activePages) {
        const [copiedPage] = await newPdf.copyPages(pdfDoc, [pageData.pageNum - 1]);
        
        if (pageData.rotation !== 0) {
          copiedPage.setRotation(degrees(pageData.rotation));
        }
        
        newPdf.addPage(copiedPage);
      }

      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Calculate remaining time to ensure minimum 3 seconds
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(3000 - elapsedTime, 0);

      // Wait for the remaining time before completing
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `organized-${file.name}`;
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error saving PDF:', error);
      alert('Error saving PDF. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="tool-page">
      <SEO 
        title="Organize PDF Pages Online – Reorder, Delete, Free"
        description="Rearrange, delete, or reorder PDF pages online free. Organize your PDF documents quickly with an easy drag-and-drop tool."
        keywords="organize pdf, reorder pdf pages, delete pdf pages online"
        path="/pdf-organize"
        schema={pdfOrganizeSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFileEarmarkText />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Organize PDF Pages
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Reorder, rotate, delete, and duplicate PDF pages visually
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          {pages.length === 0 ? (
            <motion.div
              className={`upload-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUploadCloud className="upload-icon" />
              <h3>Drop PDF file here or click to browse</h3>
              <p>Organize your PDF pages with drag & drop</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </motion.div>
          ) : (
            <div className="pdf-organize-layout">
              <div className="organize-header">
                <div className="file-info">
                  <BsFileEarmarkText />
                  <div>
                    <h3>{file.name}</h3>
                    <p>
                      {pages.filter(p => !p.deleted).length} of {pages.length} pages
                      {pages.some(p => p.deleted) && ` (${pages.filter(p => p.deleted).length} deleted)`}
                    </p>
                  </div>
                </div>
                <div className="organize-actions">
                  <button className="btn-secondary-small">
                    <BsArrowClockwise /> Reset All
                  </button>
                </div>
              </div>

              <div className="pages-organize-grid">
                <AnimatePresence>
                  {pages.map((page, index) => (
                    <motion.div
                      key={page.id}
                      className={`organize-page-card ${page.deleted ? 'deleted' : ''} ${draggedPage === index ? 'dragging' : ''}`}
                      draggable={!page.deleted}
                      onDragStart={(e) => handlePageDragStart(e, index)}
                      onDragOver={(e) => handlePageDragOver(e, index)}
                      onDragEnd={handlePageDragEnd}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: page.deleted ? 0.4 : 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2 }}
                      layout
                    >
                      {!page.deleted && (
                        <div className="drag-handle">
                          <BsArrowsMove />
                        </div>
                      )}
                      
                      <div 
                        className="page-preview"
                        style={{ transform: `rotate(${page.rotation}deg)` }}
                      >
                        {pageThumbnails[page.pageNum] ? (
                          <img 
                            src={pageThumbnails[page.pageNum]} 
                            alt={`Page ${page.pageNum}`}
                            className="page-thumbnail-image"
                          />
                        ) : (
                          <div className="page-placeholder">
                            <BsFileEarmarkText />
                            <span>Page {page.pageNum}</span>
                          </div>
                        )}
                      </div>

                      <div className="page-number-badge">
                        Page {page.pageNum}
                      </div>

                      {page.deleted ? (
                        <div className="page-actions">
                          <button
                            className="action-btn undo"
                            onClick={() => undoDelete(index)}
                            title="Undo Delete"
                          >
                            <BsArrowClockwise /> Undo
                          </button>
                        </div>
                      ) : (
                        <div className="page-actions">
                          <button
                            className="action-btn"
                            onClick={() => rotatePage(index, 90)}
                            title="Rotate 90°"
                          >
                            <FiRotateCw />
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => duplicatePage(index)}
                            title="Duplicate"
                          >
                            <BsFiles />
                          </button>
                          <button
                            className="action-btn danger"
                            onClick={() => deletePage(index)}
                            title="Delete"
                          >
                            <BsTrash />
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div className="action-buttons">
                <motion.button
                  className="btn-primary-large"
                  onClick={savePDF}
                  disabled={isProcessing || pages.filter(p => !p.deleted).length === 0}
                  whileHover={{ scale: isProcessing ? 1 : 1.02 }}
                  whileTap={{ scale: isProcessing ? 1 : 0.98 }}
                  animate={isProcessing ? {
                    boxShadow: [
                      '0 4px 20px var(--primary-shadow)',
                      '0 6px 30px var(--primary-shadow)',
                      '0 4px 20px var(--primary-shadow)'
                    ]
                  } : {}}
                  transition={{ duration: 1, repeat: isProcessing ? Infinity : 0 }}
                >
                  {isProcessing ? (
                    <>
                      <BsDownload />
                      <span className="saving-text">
                        Saving<span className="dots">...</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <BsDownload />
                      Save Organized PDF
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PDFOrganize;
